import { debounce } from 'underscore';

import { OPEN_CCD_EVENT, CLOSE_CCD_TOUR_EVENT } from '@adobe-fonts/site/events/CCDEvent.js';
import { EXPANDED_EVENT, COLLAPSED_EVENT } from '@adobe-fonts/site/events/FlyoutEvent.js';
import { EVENT_NAME as SEARCH_PARAM_CHANGED_EVENT_NAME } from '@adobe-fonts/site/events/SearchParamChangedEvent.js';
import { EVENT_NAME as SEARCH_SUBMIT_EVENT_NAME } from '@adobe-fonts/site/events/SearchSubmitEvent.js';
import { openCreativeCloudDesktop } from '../components/dialogs/FontActivationFirstMileDialog.ts';

import { getAutocompleteResults } from '../util/family_autocomplete_search_util.js';
import { trapFocus, releaseFocus } from '../util/focus.js';
import { addPageAction, setCustomAttribute, getReferrerHeader } from '../util/new_relic.js';
import { REQUIRE_LOGIN_STATE_COOKIE, REQUIRE_LOGIN_NUM_CLICKS_COOKIE } from '../util/authentication_util.js';
import { getData } from '../util/script_data.js';
import { getUserPrefs, updatePrefs } from '../util/user_prefs_util.js';
import UrlService from '../util/url_service.js';
import {
  getActivityState,
  getAdobeProfileId,
  getDaysSinceSyncActivity,
  getPlan,
  setActivityState,
  setAdobeProfileId,
  setDaysSinceSyncActivity,
  setPlan
} from '../util/user_state.js';
const cookie = require('../util/cookie.js');
const SEARCH_PARAM_CHANGE_DEBOUNCE_TIME_MS = 200;

class SitewideHeaderController {
  constructor() {
    this.element = document.querySelector('#sitewide-header');
    this.topAppBarTarget = document.querySelector('#top-app-bar-content-target');

    this.topNav = this.element.querySelector('#header-top-nav');
  }

  init() {
    if (!this.element) {
      return;
    }

    this._preloadData();
    this._setNewrelicCustomAttributes();
    this.initEventHandlers(this.element);

    // Populate the search bars once the custom element is upgraded, so the value
    // is set as a property rather than a server-rendered attribute.
    customElements.whenDefined('af-search-bar').then(() => this.initSearchBarValues());

    window.addEventListener('DOMContentLoaded', () => {
      this.initUserPrefsEventHandlers(this.element);
    });

    window.addEventListener(SEARCH_PARAM_CHANGED_EVENT_NAME, debounce((e) => {
      this._handleSearchParamChanged(e);
    }, SEARCH_PARAM_CHANGE_DEBOUNCE_TIME_MS));

    if (this.element && this.element.hasAttribute('redirectOnSubmit')) {
      window.addEventListener(SEARCH_SUBMIT_EVENT_NAME, (event) => {
        const { searchValue } = event.detail;
        if (searchValue) {
          window.location.href = `/search?query=${encodeURIComponent(searchValue)}`;
        }
      });
    } else if (this.element && this._isUnifiedBrowseEnabled()) {
      window.addEventListener(SEARCH_SUBMIT_EVENT_NAME, (event) => {
        // The submit event bubbles from the search bar up to window, so it also
        // reaches here on the unified browse page itself, where
        // UnifiedBrowseController already applies the term in place. Redirecting
        // would reload the page and repopulate the input from ?query=.
        if (document.querySelector('af-unified-browse-page')) {
          return;
        }

        const { searchValue } = event.detail;
        if (searchValue) {
          window.location.href = `/fonts?query=${encodeURIComponent(searchValue)}`;
        }
      });
    }

    // TODO(ephraim): remove this once UniversalNav is launched since we won't be
    // handling sign in this way anymore.
    const signIn = document.querySelector('.adobe-fonts-header__sign-in');
    if (!signIn) {
      return;
    }

    signIn.addEventListener('click', this._handleSignInButton);
  }

  _preloadData() {
    const userInfo = getData('/user_info');
    if (!userInfo) {
      return;
    }

    setActivityState(userInfo.activity_state);
    setAdobeProfileId(userInfo.adobe_profile_id);
    setDaysSinceSyncActivity(userInfo.days_since_sync_activity);
    setPlan(userInfo.plan);
  }

  /**
 * Handles a open CCD app button on the manage fonts menu
 */
  _handleOpenCCDButton = () => {
    addPageAction('typekit.click.use-model-font-activation-open-app', { source: 'Manage Fonts' });
    openCreativeCloudDesktop();
  };

  _setNewrelicCustomAttributes = () => {
    const referrer = UrlService.getSearchParam('referrer');
    if (referrer) {
      setCustomAttribute('referrer', referrer);
    }

    setCustomAttribute('activityState', getActivityState());
    setCustomAttribute('adobeProfileId', getAdobeProfileId());
    setCustomAttribute('daysSinceSyncActivity', getDaysSinceSyncActivity());
    setCustomAttribute('plan', getPlan());
    setCustomAttribute('referrerHeader', getReferrerHeader());
    if (cookie(REQUIRE_LOGIN_STATE_COOKIE)) {
      setCustomAttribute('requireLoginExperimentNumClicks', cookie(REQUIRE_LOGIN_NUM_CLICKS_COOKIE));
      setCustomAttribute('requireLoginExperimentState', cookie(REQUIRE_LOGIN_STATE_COOKIE));
    }
  };

  initEventHandlers = (headerEl) => {
    if (this.topNav) {
      this.topNav.addEventListener(SEARCH_PARAM_CHANGED_EVENT_NAME, this.getFamilyAutocompleteSearchResults);
    }

    headerEl.addEventListener(EXPANDED_EVENT, () => {
      trapFocus();
      this.element.inert = false;
    });

    headerEl.addEventListener(COLLAPSED_EVENT, releaseFocus);

    headerEl.addEventListener(OPEN_CCD_EVENT, this._handleOpenCCDButton);
  };

  initUserPrefsEventHandlers = async (headerEl) => {
    try {
      headerEl.addEventListener(CLOSE_CCD_TOUR_EVENT, () => {
        updatePrefs({ hideUseModelCCDTutorial: true });
      });
      const userPrefs = await getUserPrefs();
      const shouldShowCoachmark = !userPrefs.hideUseModelCCDTutorial;

      if (this.topNav) {
        this.topNav.setAttribute('showCoachmark', shouldShowCoachmark);
      }
    } catch (error) {
      if (this._isProdEnvironment()) {
        return;
      }
      console.error('Failed to initialize user prefs:', error);
    }
  };

  _handleSignInButton = () => {
    addPageAction('typekit.click.sign-in');
  };

  getFamilyAutocompleteSearchResults = async (event) => {
    const searchbar = event.target;

    try {
      const response = await getAutocompleteResults(event.detail.searchParam);
      searchbar.results = response.data;
    } catch (error) {
      if (this._isProdEnvironment()) {
        return;
      }
      console.error('Failed to get family autocomplete search results:', error);
    }
  };

  _isProdEnvironment = () => {
    return this.element.getAttribute('env') === 'prod';
  };

  /**
   * Populate the search bars on initial load. Prefer the query from the URL;
   * fall back to the server-provided value (e.g. collection pages, where the
   * value is not in the URL) delivered via a JSON island. The value is assigned
   * as a property (see _syncSearchBarValue), never written into a server-rendered
   * attribute that a client-side template engine could evaluate.
   */
  initSearchBarValues = () => {
    const decodedQuery = this.decodeSearchQuery();
    if (!decodedQuery) {
      return;
    }

    this._syncSearchBarValue(this.element.querySelector('#header-search'), decodedQuery);
    this._syncSearchBarValue(this.element.querySelector('#dialog-search-bar'), decodedQuery);
  };

  // URLSearchParams decodes the URL once; literal percent sequences stay literal.
  decodeSearchQuery = () => {
    return new URLSearchParams(window.location.search).get('query') || '';
  };

  /**
   * Sync the search bar values from the URL.
   */
  syncFromURL = () => {
    try {
      const headerSearchBar = this.element.querySelector('#header-search');
      const inlineSearchBar = this.element.querySelector('#dialog-search-bar');

      const decodedQuery = this.decodeSearchQuery();

      // Only update search bars that are NOT currently focused to avoid overriding active user input
      this._syncSearchBarValue(headerSearchBar, decodedQuery);
      this._syncSearchBarValue(inlineSearchBar, decodedQuery);

    } catch (error) {
      console.error('Error syncing from URL', error);
    }
  };

  /**
   * Safely update a search bar's value only if it's not currently focused.
   * This prevents overriding the user's active input.
   */
  _syncSearchBarValue = (searchBar, value) => {
    if (this._isContextAwareSearchEnabled() || !searchBar) {
      return;
    }

    const hasFocus = searchBar.matches(':focus-within') ||
      document.activeElement === searchBar ||
      searchBar.shadowRoot?.activeElement !== null;

    if (!hasFocus) {
      // Assign the Lit property, not the attribute: a `value` attribute would be
      // visible to client-side template engines (e.g. legacy AngularJS) as an
      // expression to evaluate; a property assignment is inert plain text.
      searchBar.value = value;
    }
  };

  /**
 * Handle search parameter changes from search bars.
 * Updates URL first, then syncs all UI from URL.
 *
 * In Unified Browse & Search, new/additive searches are triggered by the submit
 * search event.
 * So don't update the query with search param change until the user chooses
 * the options provided within context aware search.
 */
  _handleSearchParamChanged = (e) => {
    if (this._isContextAwareSearchEnabled()) {
      return;
    }

    const { searchParam } = e.detail;

    if (!searchParam) {
      UrlService.removeSearchParam('query');
    } else {
      UrlService.updateSearchParams({ query: searchParam });
    }

    this.syncFromURL();
  };

  _isContextAwareSearchEnabled = () => {
    return getData('flipper-flags')?.ContextAwareSearch;
  };

  _isUnifiedBrowseEnabled = () => {
    return getData('flipper-flags')?.UnifiedBrowse;
  };
}

export default SitewideHeaderController;
